/**
 * features.ts — per-candidate-action features (the action representation).
 *
 * The agent picks among 32 candidate leaps (8 directions × 4 lengths) each
 * tick — or 40 candidates in the knight variant (32 leaps + 8 L-shapes).
 * To make that choice LEARNABLE we describe each candidate by reading the
 * belief map (the agent's spatial memory) — NOT the area around the agent.
 * This is the design fix that cracked the problem: the old GRU took a
 * single 87-dim observation of the agent's immediate neighbourhood and had
 * to infer which leap was best from that. Here, the NN's input IS the
 * candidate — one feature row per action — and `argmax over Q(rows)` is the
 * choice. Learning "go where the believed-gold is" becomes a trivial
 * regression.
 *
 * Base row (indices [0..10], present in BOTH variants — 11 features):
 *   [0]  bGold(dest)            belief that destination cell has gold
 *   [1]  bStone(dest)           belief that destination is a stone
 *   [2]  bUnknown(dest)         destination is unexplored
 *   [3]  pathClear              1 if no believed stone blocks the action, else 0
 *   [4]  goldDensityOnPath      Σ bGold on path / length   (density, not count)
 *   [5]  unkDensityOnPath       # unknown on path / length (density, not count)
 *   [6]  recentDensityOnPath    mean bRecent on path (high = just visited)
 *   [7]  stoneDensityOnPath     Σ bStone on path / length  (risk signal)
 *   [8]  raycastUnkAhead        # unknown cells along full ray / GRID
 *   [9]  raycastGoldAhead       Σ bGold along full ray / GRID
 *   [10] invLength              1/length  (lets the model learn a length prior)
 *
 * Enriched features (indices [11..13], knight variant ONLY — 14 features).
 * The validated Python prototype (`_proto_knight_v2.py`) showed these three
 * features recover ~13% of gold on clustered worlds that the baseline
 * planner leaves on the table. They describe the knight's distinctive value:
 * reaching the EDGE of a cluster (not the centre), and what the agent will
 * face on the NEXT action after landing.
 *   [11] destNeighborhoodGold   mean bGold in 3×3 around destination
 *   [12] destLookaheadGold      mean bGold in cells 2..4 beyond destination
 *                               along the dominant move direction
 *   [13] isKnight               1.0 for knight actions, 0.0 for leaps
 *
 * Why DENSITIES (not raw counts) on the path features: an earlier prototype
 * summed raw counts, which made Q grow monotonically with leap length (longer
 * leap = more cells crossed = more coverage) and the agent degenerated to
 * "always pick length-8" (379/400 actions were length-8, 358 bumps). Density
 * normalises by length so DIRECTION carries the signal, not length.
 *
 * Why a RAYCAST beyond the leap: a length-8 leap ends at a cell 8 away, but
 * the agent should know what lies even further in that direction. The raycast
 * sums bGold and unknown-count from the agent's pose to the grid edge along
 * the direction. This is the long-range pointer that breaks ties between
 * similarly-promising short leaps.
 */

import { Belief, UNKNOWN } from './belief';
import { DIRECTIONS, N_DIRS, LEAP_LENGTHS } from './environment';
import type { Env } from './environment';
import {
  type VariantSpec,
  MOB_BASELINE,
  KNIGHT_MOVES,
  knightPaths,
  isKnightAction,
  knightMoveIdx,
  isQueenAction,
  queenDirIdx,
} from './variant';
import { densityMap, queenTarget } from './density';

/** Baseline feature count (knight variant uses 14 — see variant.ts). */
export const N_FEATURES = 11;
/** Enriched-feature count (knight variant). */
export const N_FEATURES_KNIGHT = 14;
/** Total leap-action count (also the knight-start index in the knight variant). */
export const N_ACTIONS = N_DIRS * 4; // 32 (4 leap lengths)

/** Walk up to `maxLen` cells from (r,c) in (dr,dc); return (unkCount, goldSum). */
function raycast(
  b: Belief,
  r: number,
  c: number,
  dr: number,
  dc: number,
  maxLen: number,
  grid: number,
): [number, number] {
  let unk = 0;
  let gold = 0;
  for (let k = 1; k <= maxLen; k++) {
    const nr = r + dr * k;
    const nc = c + dc * k;
    if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) break;
    const idx = nr * grid + nc;
    if (b.known[idx] === UNKNOWN) unk++;
    gold += b.bGold[idx];
  }
  return [unk, gold];
}

/**
 * Build the (spec.nActions × spec.nFeatures) candidate-feature matrix for
 * the agent's current pose. The output buffer is row-major flat — row `a`
 * starts at offset `a * spec.nFeatures`. Reuse a buffer across ticks to
 * avoid GC. The caller sizes `out` to at least
 * `spec.nActions * spec.nFeatures`.
 *
 * `spec` defaults to the baseline (32 leap-only actions × 11 features) so
 * existing callers keep working.
 *
 * Pure function of (belief, r, c). No env mutation.
 */
export function candidateFeatures(
  env: Env,
  out: Float64Array,
  spec: VariantSpec = MOB_BASELINE,
): Float64Array {
  const b = env.belief;
  const r = env.r;
  const c = env.c;
  const grid = env.world.grid;
  const nf = spec.nFeatures;
  const enriched = spec.useKnight;  // 14-feature path; writes indices [11..13]
  // For the queen variant, compute the density map once and reuse it for
  // all 8 queen actions. The buffer is allocated per-call; allocation cost
  // is negligible (360 bytes on a 20×20 grid).
  const densityBuf = spec.useQueen ? new Float64Array(grid * grid) : null;
  if (densityBuf) densityMap(env.belief, densityBuf);
  for (let a = 0; a < spec.nActions; a++) {
    const off = a * nf;

    // ── Decode the action into a destination + path + dominant direction ──
    let destR: number, destC: number;
    let pathClear: number;
    let goldSum: number, unkCount: number, recentSum: number, stoneSum: number;
    let length: number;
    // Raycast direction (unit step) — used for features [8,9] and the
    // enriched destLookaheadGold feature [12].
    let rcDr: number, rcDc: number;
    let isKnight = false;

    if (isQueenAction(a, spec)) {
      // Queen action: jump toward direction queenDirIdx(a). The target is
      // the argmax-density cell along that direction. Walk the queen path
      // and aggregate features exactly like a straight leap, but with the
      // queen's path geometry.
      const qd = queenDirIdx(a, spec);
      const [dr, dc] = DIRECTIONS[qd];
      rcDr = dr; rcDc = dc;
      // Find the target via the precomputed density map.
      // (queenTarget rescans to verify path walkability on the TRUE world;
      // the queen's pathClear reflects that.)
      const tgt = queenTarget(env, qd, densityBuf as Float64Array);
      if (tgt === null) {
        // No valid target — make it unattractive.
        destR = r + dr; destC = c + dc;  // nominally forward
        pathClear = 0;
        goldSum = unkCount = recentSum = stoneSum = 0;
        length = 1;
      } else {
        const [tr, tc] = tgt;
        destR = tr; destC = tc;
        pathClear = 1;
        length = Math.max(1, Math.max(Math.abs(tr - r), Math.abs(tc - c)));
        // Aggregate over the queen-walk path: walk cell-by-cell along the
        // dominant axis (both axes for diagonals), summing belief features.
        goldSum = unkCount = recentSum = stoneSum = 0;
        let cr = r, cc = c;
        let pathLen = 0;
        while ((cr !== tr || cc !== tc) && pathLen < length) {
          const sdr = tr === cr ? 0 : (tr > cr ? 1 : -1);
          const sdc = tc === cc ? 0 : (tc > cc ? 1 : -1);
          cr += sdr; cc += sdc;
          pathLen++;
          if (cr < 0 || cr >= grid || cc < 0 || cc >= grid) break;
          const idx = cr * grid + cc;
          goldSum += b.bGold[idx];
          stoneSum += b.bStone[idx];
          recentSum += b.bRecent[idx];
          if (b.known[idx] === UNKNOWN) unkCount++;
        }
        // Use the actual walked length for density normalization (handles
        // the rare case where the path clipped shorter than Chebyshev).
        length = Math.max(1, pathLen);
      }
    } else if (isKnightAction(a, spec)) {
      // Knight L-shape: walk whichever candidate path is clear.
      isKnight = true;
      const mi = knightMoveIdx(a, spec);
      const [pathA, pathB] = knightPaths(r, c, mi);
      const cellClear = (rr: number, cc: number) =>
        rr >= 0 && rr < grid && cc >= 0 && cc < grid && b.bStone[rr * grid + cc] < 0.5;
      const clearA = pathA.every(([rr, cc]) => cellClear(rr, cc));
      const clearB = pathB.every(([rr, cc]) => cellClear(rr, cc));
      pathClear = clearA || clearB ? 1 : 0;
      const path = clearA ? pathA : clearB ? pathB : pathA;
      destR = path[path.length - 1][0];
      destC = path[path.length - 1][1];
      length = 3;
      // Aggregate over the chosen path.
      goldSum = unkCount = recentSum = stoneSum = 0;
      for (const [nr, nc] of path) {
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
        const idx = nr * grid + nc;
        goldSum += b.bGold[idx];
        stoneSum += b.bStone[idx];
        recentSum += b.bRecent[idx];
        if (b.known[idx] === UNKNOWN) unkCount++;
      }
      // Raycast along the long-leg direction past the destination.
      const [kdr, kdc] = KNIGHT_MOVES[mi];
      if (Math.abs(kdr) === 2) {
        rcDr = kdr > 0 ? 1 : -1; rcDc = 0;
      } else {
        rcDr = 0; rcDc = kdc > 0 ? 1 : -1;
      }
    } else {
      // Straight leap.
      const dir = a % N_DIRS;
      const lenIdx = (a / N_DIRS) | 0;
      length = LEAP_LENGTHS[lenIdx];
      const [dr, dc] = DIRECTIONS[dir];
      rcDr = dr; rcDc = dc;
      destR = r + dr * length;
      destC = c + dc * length;

      pathClear = 1;
      goldSum = unkCount = recentSum = stoneSum = 0;
      for (let k = 1; k <= length; k++) {
        const nr = r + dr * k;
        const nc = c + dc * k;
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) {
          pathClear = 0;
          break;
        }
        const idx = nr * grid + nc;
        if (b.bStone[idx] >= 0.5) pathClear = 0;
        goldSum += b.bGold[idx];
        stoneSum += b.bStone[idx];
        if (b.known[idx] === UNKNOWN) unkCount++;
        recentSum += b.bRecent[idx];
      }
    }

    const invLen = 1 / length;

    // Destination features (cells beyond sensing range just read the prior).
    if (destR >= 0 && destR < grid && destC >= 0 && destC < grid) {
      const di = destR * grid + destC;
      out[off + 0] = b.bGold[di];
      out[off + 1] = b.bStone[di];
      out[off + 2] = b.known[di] === UNKNOWN ? 1 : 0;
    } else {
      // Out of bounds — make it unattractive by marking it as believed stone.
      out[off + 0] = 0;
      out[off + 1] = 1;
      out[off + 2] = 0;
    }

    out[off + 3] = pathClear;
    out[off + 4] = goldSum * invLen;
    out[off + 5] = unkCount * invLen;
    out[off + 6] = recentSum * invLen;
    out[off + 7] = stoneSum * invLen;

    // Raycast to the grid edge along the dominant direction. For knight
    // actions the dominant direction is the long-leg axis.
    const [unkAhead, goldAhead] = raycast(b, r, c, rcDr, rcDc, grid - 1, grid);
    out[off + 8] = unkAhead / grid;
    out[off + 9] = goldAhead / grid;
    out[off + 10] = invLen;

    if (enriched) {
      // [11] destNeighborhoodGold — mean bGold in 3×3 around destination.
      if (destR >= 0 && destR < grid && destC >= 0 && destC < grid) {
        let nbSum = 0;
        let nbN = 0;
        for (let dr2 = -1; dr2 <= 1; dr2++) {
          for (let dc2 = -1; dc2 <= 1; dc2++) {
            const nr = destR + dr2;
            const nc = destC + dc2;
            if (nr >= 0 && nr < grid && nc >= 0 && nc < grid) {
              nbSum += b.bGold[nr * grid + nc];
              nbN++;
            }
          }
        }
        out[off + 11] = nbN > 0 ? nbSum / nbN : 0;
        // [12] destLookaheadGold — mean bGold 2..4 cells beyond destination.
        let laSum = 0;
        let laN = 0;
        for (let k = 2; k <= 4; k++) {
          const nr = destR + rcDr * k;
          const nc = destC + rcDc * k;
          if (nr >= 0 && nr < grid && nc >= 0 && nc < grid) {
            laSum += b.bGold[nr * grid + nc];
            laN++;
          }
        }
        out[off + 12] = laN > 0 ? laSum / laN : 0;
      } else {
        out[off + 11] = 0;
        out[off + 12] = 0;
      }
      // [13] isKnight — lets the model learn a knight-specific intercept.
      out[off + 13] = isKnight ? 1 : 0;
    }
  }
  return out;
}
